import type { AxiosInstance } from 'axios';
import type { paths, components } from '../schema';
import type { FieldValue } from '../types/field-value';
import type {
  PaginatedResponse,
  RequestOptions,
  RequestOptionsWithParams,
} from './types';
import { curry1 } from './utils';

type TeamMemberFieldValue = Exclude<FieldValue, { field_type: 'relationship' }>;

type TeamMemberRequest = components['schemas']['EmployeeRequest'];

export interface CurrentTeamMember
  extends Omit<
    Mutable<components['schemas']['EmployeeSerializerV2']>,
    'fields' | 'mfa_setting' | 'toolbar_config'
  > {
  fields: Record<string, TeamMemberFieldValue>;
  mfa_setting: {
    business: 'disabled' | 'enabled';
    totp: 'disabled' | 'enabled';
  };
  toolbar_config: {
    elements: Record<string, any>[];
    modified: boolean;
  };
}

type TeamMemberPublic =
  components['schemas']['PaginatedEmployeePublicListList'];

export interface TeamMember
  extends Omit<Mutable<components['schemas']['ListEmployee']>, 'fields'> {
  fields: Record<string, TeamMemberFieldValue>;
}

export interface CreateTeamMemberData
  extends Omit<
    Mutable<components['schemas']['EmployeeWriteRequest']>,
    'fields' | 'annual_sales_goal'
  > {
  annual_sales_goal?: number | null;
  fields: { id: string; value: TeamMemberFieldValue['value'] }[];
}

export type GetTeamMembersResponse<
  TParams extends GetTeamMembersParams | undefined = undefined,
> = TParams extends { page_size: number }
  ? PaginatedResponse<TeamMember>
  : TeamMember[];

type TeamMemberSort = Exclude<
  Exclude<
    paths['/api/team']['get']['parameters']['query'],
    undefined
  >['ordering'],
  undefined
>;

type TeamMembersParams = Omit<
  Exclude<paths['/api/team']['get']['parameters']['query'], undefined>,
  'ordering'
> & {
  ordering?: TeamMemberSort | `-${TeamMemberSort}`;
};

/**
 * The backend API returns a paginated response only when the page_size parameter is present.
 * The generic 'paginated' argument forces the type of page_size to be required. This is needed when
 * the params argument is not inlined in the function call for the api and query utilities to make the
 * return type inference in {@link GetTeamMembersResponse} will give you the correct result
 */
export type GetTeamMembersParams<
  T extends 'paginated' | undefined = undefined,
> = T extends 'paginated'
  ? Pretty<Omit<TeamMembersParams, 'page_size'> & { page_size: number }>
  : TeamMembersParams;

type SearchTeamMembersParams = Exclude<
  paths['/api/team/search']['post']['parameters']['query'],
  undefined
>;

export interface SearchTeamMembersOptions
  extends RequestOptionsWithParams<SearchTeamMembersParams> {
  body?: {
    and: boolean;
    query: Required<
      paths['/api/team/search']['post']
    >['requestBody']['content']['application/json']['query'];
  };
}

export const createTeamMember = async (
  instance: AxiosInstance,
  body: CreateTeamMemberData,
  options: RequestOptions = {}
): Promise<CurrentTeamMember> => {
  const res = await instance.post('/team', body, options);

  return res.data;
};

const getPublicTeamMembers = async (
  instance: AxiosInstance,
  businessId: string,
  formFieldId: string,
  options?: RequestOptionsWithParams<
    paths['/api/public/{business_pk}/{form_field_id}/teams']['get']['parameters']['query']
  >
): Promise<TeamMemberPublic> => {
  const res = await instance.get<TeamMemberPublic>(
    `/public/${businessId}/${formFieldId}/teams`,
    options
  );

  return res.data;
};

export const getTeamMembers = async <
  TParams extends GetTeamMembersParams | undefined = undefined,
>(
  instance: AxiosInstance,
  options: RequestOptionsWithParams<TParams> = {}
): Promise<GetTeamMembersResponse<TParams>> => {
  const res = await instance.get<GetTeamMembersResponse<TParams>>(
    '/team',
    options
  );

  return res.data;
};

export const searchTeamMembers = async (
  instance: AxiosInstance,
  options: SearchTeamMembersOptions = {}
): Promise<PaginatedResponse<TeamMember>> => {
  const { body = {}, ...opts } = options;

  const res = await instance.post<PaginatedResponse<TeamMember>>(
    '/team/search',
    body,
    opts
  );

  return res.data;
};

export const getCurrentTeamMember = async (
  instance: AxiosInstance,
  options?: RequestOptions
): Promise<CurrentTeamMember> => {
  const res = await instance.get<CurrentTeamMember>(
    '/team/mine?v2=true',
    options
  );

  return res.data;
};

export const updateTeamMember = async (
  instance: AxiosInstance,
  id: string,
  data: TeamMemberRequest,
  options?: RequestOptions
): Promise<CurrentTeamMember> => {
  const res = await instance.put<CurrentTeamMember>(
    `/team/${id}`,
    data,
    options
  );

  return res.data;
};

export class TeamMemberService {
  public createTeamMember;
  public getCurrentTeamMember;
  public getPublicTeamMembers;
  public getTeamMembers;
  public seaerchTeamMembers;
  public updateTeamMember;

  constructor(private instance: AxiosInstance) {
    this.createTeamMember = curry1(createTeamMember)(this.instance);
    this.getCurrentTeamMember = curry1(getCurrentTeamMember)(this.instance);
    this.getPublicTeamMembers = curry1(getPublicTeamMembers)(this.instance);
    this.getTeamMembers = curry1(getTeamMembers)(this.instance);
    this.seaerchTeamMembers = curry1(searchTeamMembers)(this.instance);
    this.updateTeamMember = curry1(updateTeamMember)(this.instance);
  }
}
